import React, { useState, useMemo, useEffect } from 'react';
import { Table, Button, Popconfirm, message, Checkbox } from 'antd';
import { MSG_SAVE_SUCCESS, MSG_ERROR } from '../../../../../../../constants';
import { getApiErrorMessage, getApiResponseMessage, wasBusinessUnauthorizedHandled } from '../../../../../../../utils/httpError';
import {
  canCancelRevokedUnusedPromo,
  getPromoCodeId,
  getPromoRemainingCount,
  isRevokedPromoRecord,
} from './promoCodeCancelUtils';
import HelpTip from './HelpTip';
import { PROMO_COLUMN_HELP } from '../brokerageSubscriptionHelp';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PLACEHOLDER_LINK = 'TO BE ADDED';

const toFilters = (arr) =>
  [...new Set(arr.map((v) => (v != null && v !== '' ? String(v) : '—')))].sort().map((v) => ({ text: v, value: v }));

function getPromoCodeValue(record) {
  return record?.promoCode ?? record?.code ?? '';
}

function getPromoCodeColumnValue(record) {
  return getPromoCodeValue(record) || '—';
}

function getPromoRegistrationLink(record) {
  const link = record?.promoCodeRegistrationLink ?? record?.link;
  return link && link !== PLACEHOLDER_LINK ? link : null;
}

const promoCopyValue = (record) => getPromoRegistrationLink(record) || getPromoCodeValue(record);

const formatCreatedBy = (record) => {
  const name = record.createdBy || record.brokerName;
  if (!name) return '—';

  if (!name.includes('@')) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0).toUpperCase()}. ${parts[parts.length - 1]}`;
    }
    return name;
  }

  const local = name.split('@')[0];
  const dotParts = local.split(/[._-]+/).filter(Boolean);
  if (dotParts.length >= 2) {
    const first = dotParts[0].charAt(0).toUpperCase();
    const last =
      dotParts[dotParts.length - 1].charAt(0).toUpperCase() +
      dotParts[dotParts.length - 1].slice(1).toLowerCase();
    return `${first}. ${last}`;
  }
  return local;
};

const getPromoStatusLabel = (record) => {
  if (isRevokedPromoRecord(record)) return 'Revoked';
  if (record.isExpired) return 'Expired';
  return 'Active';
};

const getRemainingColumnValue = (record) => {
  const value = record.remaining;
  return value != null ? String(value) : '—';
};

const getPromoExpiryColumnValue = (record) => record.expiryDate || '—';

const colTitle = (short, full = short, help) => (
  <span className="promo-th-label" title={help ? undefined : full}>
    {short}
    {help ? <HelpTip title={help} /> : null}
  </span>
);

const SORTERS = {
  code: (a, b) => getPromoCodeColumnValue(a).localeCompare(getPromoCodeColumnValue(b)),
  createdBy: (a, b) => formatCreatedBy(a).localeCompare(formatCreatedBy(b)),
  quantity: (a, b) => (a.quantity ?? 0) - (b.quantity ?? 0),
  remaining: (a, b) => (a.remaining ?? 0) - (b.remaining ?? 0),
  promoExpiryDate: (a, b) => {
    const ta = a.exactExpiryDate ? new Date(a.exactExpiryDate).getTime() : 0;
    const tb = b.exactExpiryDate ? new Date(b.exactExpiryDate).getTime() : 0;
    return ta - tb;
  },
};

const applyFilters = (data, filters) => {
  let result = data;

  if (filters.code?.length) {
    result = result.filter((r) => filters.code.includes(getPromoCodeColumnValue(r)));
  }
  if (filters.createdBy?.length) {
    result = result.filter((r) => filters.createdBy.includes(formatCreatedBy(r)));
  }
  if (filters.status?.length) {
    result = result.filter((r) => filters.status.includes(getPromoStatusLabel(r)));
  }
  if (filters.remaining?.length) {
    result = result.filter((r) => filters.remaining.includes(getRemainingColumnValue(r)));
  }
  if (filters.promoExpiryDate?.length) {
    result = result.filter((r) => filters.promoExpiryDate.includes(getPromoExpiryColumnValue(r)));
  }

  return result;
};

const applySort = (data, key, order) => {
  if (!key || !order) return data;
  const fn = SORTERS[key];
  if (!fn) return data;
  const sorted = [...data].sort(fn);
  return order === 'descend' ? sorted.reverse() : sorted;
};

export default ({ promoCodes = [], revoke, reinstate, cancelUnused }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sorterKey, setSorterKey] = useState(null);
  const [sorterOrder, setSorterOrder] = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [promoCodes.length]);

  const onRevoke = async ({ code }) => {
    try {
      await revoke({ promoCode: code });
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onReinstate = async (record) => {
    const code = getPromoCodeValue(record);
    if (!code) {
      message.error('This promo code cannot be reinstated because no promo code value was found.');
      return;
    }

    try {
      await reinstate({ promoCode: code });
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onCancelUnused = async (record) => {
    const promoCodeId = getPromoCodeId(record);
    const count = getPromoRemainingCount(record);
    if (!promoCodeId || count <= 0 || !cancelUnused) {
      return;
    }

    try {
      const res = await cancelUnused({ promoCodeId, count });
      message.success(getApiResponseMessage(res?.data, 'Unused promo keys cancelled successfully.'));
    } catch (err) {
      if (!wasBusinessUnauthorizedHandled(err)) {
        message.error(getApiErrorMessage(err, MSG_ERROR));
      }
    }
  };

  const canCancelUnused = (record) => canCancelRevokedUnusedPromo(record);

  const onCopy = (record) => {
    const value = promoCopyValue(record);
    if (!value) {
      return;
    }
    const input = document.createElement('input');
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    message.success(getPromoRegistrationLink(record) ? 'Promo link copied' : 'Promo code copied');
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const nextSorterKey = sorter.columnKey || null;
    const nextSorterOrder = sorter.order || null;
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(activeFilters);
    const sorterChanged = nextSorterKey !== sorterKey || nextSorterOrder !== sorterOrder;

    setActiveFilters(filters);
    setSorterKey(nextSorterKey);
    setSorterOrder(nextSorterOrder);

    if (pagination && pagination.pageSize) setPageSize(Number(pagination.pageSize));
    setPage(filtersChanged || sorterChanged ? 1 : pagination.current || 1);
  };

  const sortOrder = (key) => (sorterKey === key ? sorterOrder : false);
  const filteredValue = (key) => (activeFilters[key]?.length ? activeFilters[key] : null);

  const visiblePromoCodes = useMemo(
    () => (showExpired ? promoCodes : promoCodes.filter((p) => !p.isExpired)),
    [promoCodes, showExpired],
  );

  const codeFilters = useMemo(
    () => toFilters(visiblePromoCodes.map(getPromoCodeColumnValue)),
    [visiblePromoCodes],
  );
  const createdByFilters = useMemo(
    () => toFilters(visiblePromoCodes.map(formatCreatedBy)),
    [visiblePromoCodes],
  );
  const statusFilters = useMemo(
    () => toFilters(visiblePromoCodes.map(getPromoStatusLabel)),
    [visiblePromoCodes],
  );
  const remainingFilters = useMemo(
    () => toFilters(visiblePromoCodes.map(getRemainingColumnValue)),
    [visiblePromoCodes],
  );
  const promoExpiryDateFilters = useMemo(
    () => toFilters(visiblePromoCodes.map(getPromoExpiryColumnValue)),
    [visiblePromoCodes],
  );

  const columns = [
    {
      key: 'code',
      title: colTitle('Code'),
      dataIndex: 'code',
      className: 'promo-code-col',
      width: 152,
      sorter: true,
      sortOrder: sortOrder('code'),
      filters: codeFilters,
      filteredValue: filteredValue('code'),
      render: (_, record) => {
        const value = getPromoCodeColumnValue(record);
        return (
          <strong className="promo-code-cell" title={value !== '—' ? value : undefined}>
            {value}
          </strong>
        );
      },
    },
    {
      key: 'createdBy',
      title: colTitle('Created', 'Created by'),
      className: 'promo-created-col',
      width: 116,
      sorter: true,
      sortOrder: sortOrder('createdBy'),
      filters: createdByFilters,
      filteredValue: filteredValue('createdBy'),
      render: (_, record) => (
        <span className="promo-created-cell" title={formatCreatedBy(record)}>
          {formatCreatedBy(record)}
        </span>
      ),
    },
    {
      key: 'quantity',
      title: colTitle('Purchased qty', 'Purchased quantity', PROMO_COLUMN_HELP.quantity),
      dataIndex: 'quantity',
      className: 'promo-qty-col',
      width: 132,
      align: 'center',
      sorter: true,
      sortOrder: sortOrder('quantity'),
      render: (v) => (v != null ? v : '—'),
    },
    {
      key: 'remaining',
      title: colTitle('Rem.', 'Remaining', PROMO_COLUMN_HELP.remaining),
      dataIndex: 'remaining',
      className: 'promo-remaining-col',
      width: 96,
      align: 'center',
      sorter: true,
      sortOrder: sortOrder('remaining'),
      filters: remainingFilters,
      filteredValue: filteredValue('remaining'),
      render: (v) => (v != null ? v : '—'),
    },
    {
      key: 'promoExpiryDate',
      title: colTitle('Expiry', 'Promo expiry date', PROMO_COLUMN_HELP.expiry),
      dataIndex: 'expiryDate',
      className: 'promo-expiry-col',
      width: 108,
      align: 'center',
      sorter: true,
      sortOrder: sortOrder('promoExpiryDate'),
      filters: promoExpiryDateFilters,
      filteredValue: filteredValue('promoExpiryDate'),
      render: (_, record) => getPromoExpiryColumnValue(record),
    },
    {
      key: 'status',
      title: colTitle('Status', 'Status', PROMO_COLUMN_HELP.status),
      className: 'promo-status-col',
      width: 120,
      align: 'center',
      filters: statusFilters,
      filteredValue: filteredValue('status'),
      render: (_, record) => {
        const label = getPromoStatusLabel(record);
        const mod =
          label === 'Revoked' ? 'revoked' : label === 'Expired' ? 'expired' : 'active';
        return <span className={`promo-status promo-status--${mod}`}>{label}</span>;
      },
    },
    {
      key: 'cancelUnused',
      title: colTitle('Cancel', 'Cancel unused keys', PROMO_COLUMN_HELP.cancel),
      className: 'promo-action-col',
      width: 104,
      align: 'center',
      render: (_, record) => {
        if (!canCancelUnused(record)) {
          return <span className="promo-na">—</span>;
        }

        const count = getPromoRemainingCount(record);
        return (
          <Popconfirm
            title={`Cancel ${count} unused promo key${count === 1 ? '' : 's'}?`}
            okText="Cancel"
            cancelText="Keep"
            onConfirm={() => onCancelUnused(record)}
          >
            <Button size="small" className="promo-cancel-btn">
              Cancel
            </Button>
          </Popconfirm>
        );
      },
    },
    {
      key: 'revoke',
      title: colTitle('Revoke', 'Revoke', PROMO_COLUMN_HELP.revoke),
      className: 'promo-action-col',
      width: 104,
      align: 'center',
      render: (_, record) =>
        record.isRevoked ? (
          <span className="promo-na">—</span>
        ) : (
          <Popconfirm
            title="Revoke this promo code?"
            onConfirm={() => onRevoke(record)}
            okText="Revoke"
            cancelText="Cancel"
          >
            <Button size="small" className="promo-revoke-btn">
              Revoke
            </Button>
          </Popconfirm>
        ),
    },
    {
      key: 'reinstate',
      title: colTitle('Reinstate', 'Reinstate', PROMO_COLUMN_HELP.reinstate),
      className: 'promo-action-col',
      width: 120,
      align: 'center',
      render: (_, record) =>
        record.isRevoked && reinstate ? (
          <Popconfirm
            title="Reinstate this promo code?"
            onConfirm={() => onReinstate(record)}
            okText="Reinstate"
            cancelText="Cancel"
          >
            <Button size="small" className="promo-reinstate-btn">
              Reinstate
            </Button>
          </Popconfirm>
        ) : (
          <span className="promo-na">—</span>
        ),
    },
    {
      key: 'copyLink',
      title: colTitle('Copy', 'Copy registration link'),
      className: 'promo-action-col',
      width: 76,
      align: 'center',
      render: (_, record) => (
        <Button size="small" className="promo-copy-btn" onClick={() => onCopy(record)}>
          Copy
        </Button>
      ),
    },
  ];

  const filtered = useMemo(
    () => applySort(applyFilters(visiblePromoCodes, activeFilters), sorterKey, sorterOrder),
    [visiblePromoCodes, activeFilters, sorterKey, sorterOrder],
  );

  const paged = filtered;

  return (
    <div className="promo-codes-wrap">
      <div className="table-search-container">
        <Checkbox
          checked={showExpired}
          onChange={(e) => {
            const nextShowExpired = e.target.checked;
            setShowExpired(nextShowExpired);
            if (!nextShowExpired) {
              setActiveFilters((prev) => {
                const status = (prev.status || []).filter((value) => value !== 'Expired');
                if (status.length === (prev.status || []).length) return prev;
                return { ...prev, status: status.length ? status : null };
              });
            }
            setPage(1);
          }}
          className="table-toggle"
        >
          Show Expired
        </Checkbox>
      </div>
      <Table
        columns={columns}
        dataSource={paged}
        rowKey="promoCodeId"
        pagination={{
          current: page,
          pageSize,
          total: filtered.length,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          hideOnSinglePage: false,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} promo codes`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1128 }}
        locale={{
          emptyText: promoCodes.length ? 'No results match your filters.' : 'No promo codes',
        }}
      />
    </div>
  );
};
